import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Layers, Package, Search, Trophy, Users } from 'lucide-react';
import { getOperationRankings } from '@/services/payrollApi';
import { RankingsControls } from '@/components/opRankings/RankingsControls';
import { CoverageStrip } from '@/components/opRankings/CoverageStrip';
import { OperationTabs } from '@/components/opRankings/OperationTabs';
import { RankingsTable } from '@/components/opRankings/RankingsTable';
import { exportRankingsCsv } from '@/components/opRankings/exportRankingsCsv';
import { METRICS, PRESETS, fmtQty, fmtVal, istToday, lowRuleText } from '@/components/opRankings/fmt';

const Tile = ({ icon: Icon, label, value, tone = 'text-gray-900', testId }) => (
  <div className="bg-white rounded-lg border border-gray-200 px-4 py-2.5 shadow-sm min-w-[140px]">
    <div className="text-xs text-gray-400 flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
    <div className={`text-xl font-bold tabular-nums ${tone}`} data-testid={testId}>{value}</div>
  </div>
);

const initial = (lockModule) => {
  const [from, to] = PRESETS[2].range(istToday());
  return { from, to, preset: 'fy', module: lockModule || 'all', rankBy: 'per_day', lowRule: 'avg', lowPct: 70, minDays: 3, shift: 'all', base: 'all' };
};

const filterText = (data) => {
  const sh = (data.shifts || []).find((s) => s.id === data.shift);
  const base = data.base_section === data.no_base ? 'no base section' : data.base_section;
  return [sh && `${sh.name} shift only`, data.base_section && data.base_section !== 'all' && `base section: ${base} (ranked among themselves)`]
    .filter(Boolean).join(' · ');
};

const OpHeader = ({ op, data, search, setSearch, repeatOnly, setRepeatOnly }) => (
  <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="rank-op-header">
    <div className="mr-auto">
      <div className="text-lg font-bold text-gray-900" data-testid="rank-op-name">{op.name} <span className="text-sm font-medium text-gray-400">{op.module}</span></div>
      <div className="text-xs text-gray-500" data-testid="rank-op-meta">
        Target {op.target_per_hour ? `${fmtQty(op.target_per_hour)} pcs/hr (${fmtQty(op.target_per_day)} / day)` : 'not set'} ·
        Period average {fmtVal(op.avg, data.rank_by)} {METRICS[data.rank_by].short} · {op.ranked} ranked of {op.staff} ·
        Low = {lowRuleText(data.low_rule, data.low_pct)}
        {filterText(data) && <span className="ml-1 font-medium text-indigo-600" data-testid="rank-active-filters">· {filterText(data)}</span>}
      </div>
    </div>
    <div className="relative">
      <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search staff" data-testid="rank-search-input"
        className="border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm w-52 focus:ring-2 focus:ring-indigo-500 outline-none" />
    </div>
    <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
      <input type="checkbox" checked={repeatOnly} onChange={(e) => setRepeatOnly(e.target.checked)} className="accent-rose-600 w-4 h-4" data-testid="rank-repeat-only" />
      Repeat low only
    </label>
  </div>
);

const OperationRankings = ({ lockModule = null, backRoute = '/sections/accounts', backLabel = 'Accounts' }) => {
  const navigate = useNavigate();
  const [f, setF] = useState(() => initial(lockModule));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [opCode, setOpCode] = useState('');
  const [search, setSearch] = useState('');
  const [repeatOnly, setRepeatOnly] = useState(false);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError('');
    try {
      setData(await getOperationRankings({ date_from: f.from, date_to: f.to, module: f.module, rank_by: f.rankBy,
        low_rule: f.lowRule, low_pct: f.lowPct, min_days: f.minDays, shift: f.shift, base_section: f.base,
        ...(refresh ? { refresh: true } : {}) }));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load the rankings.');
    } finally {
      setLoading(false);
    }
  }, [f]);
  useEffect(() => { if (f.from && f.to) load(); }, [load, f.from, f.to]);

  const ops = data?.operations || [];
  const op = ops.find((o) => o.code === opCode) || ops[0];
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (op?.rows || []).filter((r) => (!repeatOnly || r.repeat_low)
      && (!q || `${r.staff_name} ${r.staff_id}`.toLowerCase().includes(q)));
  }, [op, search, repeatOnly]);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="op-rankings-page">
      <div className="max-w-[1600px] mx-auto px-4 py-6">
        <button onClick={() => navigate(backRoute)} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-3" data-testid="rank-back-btn">
          <ArrowLeft className="w-4 h-4" /> {backLabel}
        </button>
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2.5 rounded-xl bg-indigo-600 text-white shadow"><Trophy className="w-6 h-6" /></div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="rank-title">{lockModule ? `${lockModule} — Staff Rankings` : 'Staff Rankings by Operation'}</h1>
            <p className="text-gray-500 text-sm">Monthly productivity per operation — spot staff who stay low {data?.repeat_months || 3} months in a row</p>
          </div>
        </div>

        <RankingsControls f={f} setF={setF} modules={data?.modules || []} shifts={data?.shifts || []} baseSections={data?.base_sections || []}
          noBase={data?.no_base} lockModule={lockModule} loading={loading}
          onRefresh={() => load(true)} onExport={() => exportRankingsCsv(data)} canExport={!!ops.length} />

        {error && <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="rank-error">{error}</div>}

        {data && (
          <>
            <CoverageStrip months={data.months} />
            <div className="flex flex-wrap gap-3 mb-4">
              <Tile icon={Users} label="Staff ranked" value={data.summary.staff} testId="rank-summary-staff" />
              <Tile icon={AlertTriangle} label={`Low ${data.repeat_months} months in a row`} value={data.summary.repeat_low_staff} tone="text-rose-600" testId="rank-summary-repeat" />
              <Tile icon={Layers} label="Operations" value={data.summary.operations} testId="rank-summary-ops" />
              <Tile icon={Package} label="Pieces" value={fmtQty(data.summary.qty)} tone="text-emerald-600" testId="rank-summary-qty" />
            </div>
            {ops.length ? (
              <>
                <OperationTabs ops={ops} active={op.code} onPick={setOpCode} />
                <OpHeader op={op} data={data} search={search} setSearch={setSearch} repeatOnly={repeatOnly} setRepeatOnly={setRepeatOnly} />
                <RankingsTable op={op} rows={rows} months={data.months} metric={data.rank_by} need={data.repeat_months} />
                <p className="text-xs text-gray-500 mt-3" data-testid="rank-legend">
                  Red cell = low performer that month ({lowRuleText(data.low_rule, data.low_pct)}). Grey = fewer than {data.min_days} days in the operation (not judged or ranked).
                  H / L = the staff&apos;s best and lowest day that month. Hover a cell for days, hours and pieces.
                </p>
              </>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 px-4 py-12 text-center text-gray-400" data-testid="rank-no-data">No operation work in this period</div>
            )}
          </>
        )}
        {loading && !data && <div className="py-16 text-center text-gray-400" data-testid="rank-loading">Loading rankings…</div>}
      </div>
    </div>
  );
};

export default OperationRankings;
